/**
 * Closing tab → Settlement workbook: the closer's pre-close HUD / ALTA
 * balancing workbook (eastview-os frontend_documentation/settlement-workbook.md).
 *
 * Two states:
 *   • no workbook yet — the SOURCE PICKER: the loan's documents (settlement
 *     statements first), an upload with the backend's `uploadDocTypes`, and a
 *     Build that reads the chosen document (`workbook_extract`, 30–90 s) or
 *     seeds quickly from the listener's existing extract (`hud_extract`).
 *   • a workbook — three panes: the PDF (left; a line click jumps it to the
 *     line's page), the lines grid (middle; inline edit, bulk grouping, revert,
 *     add line) and the balance panel (right; printed-total checks, cash to
 *     close, the wire tie-out against the Net Wire Calculator).
 *
 * Every response carries the whole workbook and it REPLACES local state — no
 * merging (doc § Response shape). Select lists come from GET /options, never
 * hard-coded. Nothing here writes the loan or the Fees tab; the bucket column
 * is labelled "Fees tab (preview)" to say so. Pre-close only — the Closing tab
 * hides the sub-tab on acquisitions, and the routes 409 there.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import UndoRounded from "@mui/icons-material/UndoRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { losExtractsApi } from "../../../../api/losExtractsApi";
import {
  BUILD_SOURCES,
  settlementWorkbookApi,
  workbookErrorMessage,
} from "../../../../api/settlementWorkbookApi";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import {
  FormChoiceCard,
  FormNote,
  FormOptionChips,
  FormPill,
  FormWarn,
} from "../../../../components/forms/FormPrimitives";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import ConfirmationDialog from "../../../../components/ui/ConfirmationDialog";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { formSx } from "../../../../theme/formStyles";
import { ActionCell, actionColumn } from "../../../admin/shared/adminTableActions";
import HudSourceChip from "../LoanDrilldownWireAndFundingTab/panes/HudSourceChip";
import { Pill, SectionHead, T, labelSx, money } from "../LoanDrilldownWireAndFundingTab/wireFundingShared";

const stamp = () => new Date().toISOString().replace(/[:.]/g, "-");

const MONEY_COLS = ["borrowerDebit", "borrowerCredit", "sellerDebit", "sellerCredit"];
const COLUMN_LABEL = {
  borrowerDebit: "Borrower debit",
  borrowerCredit: "Borrower credit",
  sellerDebit: "Seller debit",
  sellerCredit: "Seller credit",
  poc: "Paid outside closing",
  pocAmount: "Paid outside closing",
};
// Statement fields an edit can change; `extracted` holds their original values.
const STATEMENT_FIELDS = [
  "lineNumber",
  "section",
  "description",
  "lineCategory",
  "paidTo",
  "payeeRole",
  "paidBy",
  ...MONEY_COLS,
  "pocAmount",
  "pocPaidBy",
  "page",
];

const fmtVal = (v) =>
  v == null || v === "" ? "—" : typeof v === "number" ? money(v) : String(v);

/** "description: 'A' → 'B'" lines for the edited-row tooltip. */
const diffLines = (line) => {
  const ex = line?.extracted || {};
  return STATEMENT_FIELDS.filter((f) => (line?.[f] ?? null) !== (ex[f] ?? null)).map(
    (f) => `${f}: ${fmtVal(ex[f])} → ${fmtVal(line?.[f])}`,
  );
};

const toNumOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};

/** ✓ or the variance, zero tolerance. */
const TieMark = ({ ties, variance }) =>
  ties == null ? (
    <Txt sx={{ fontSize: 12, color: T.faint }}>—</Txt>
  ) : ties ? (
    <Flx ac gap={0.5}>
      <CheckCircleRounded sx={{ fontSize: 15, color: T.green }} />
      <Txt sx={{ fontSize: 12, fontWeight: 600, color: T.green }}>ties</Txt>
    </Flx>
  ) : (
    <Txt sx={{ fontFamily: T.mono, fontSize: 12, fontWeight: 700, color: T.red }}>
      {money(variance)}
    </Txt>
  );

/* ────────────────────────── source picker / build ────────────────────────── */

const SourcePicker = ({ dealId, exists, onBuilt, onCancel }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { uploadLoanDocs } = useLoanFilesHook();
  const { getLoanById } = useUnderwritingHook();
  const loanDocuments = useSelector((s) => s.loanDrilldown?.loanDocuments);
  const groupsAndTypes = useSelector((s) => s.fetchedSelections?.fileDocGroupAndTypes);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState(null);
  const [mode, setMode] = useState(BUILD_SOURCES.WORKBOOK_EXTRACT);
  const [building, setBuilding] = useState(false);
  const [rejection, setRejection] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [uploadType, setUploadType] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settlementWorkbookApi.listDocuments(dealId);
      setData(res);
      setUploadType((t) => t || res?.uploadDocTypes?.[0] || "");
      // Preselect: the pre-close preliminary (not executed) statement first —
      // the doc's own preference — else the first settlement statement.
      setChoice((c) => {
        if (c) return c;
        const stmts = (res?.documents || []).filter((d) => d.isSettlementStatement);
        return (
          stmts.find((d) => d.isExecuted === false)?.documentId ||
          stmts[0]?.documentId ||
          null
        );
      });
    } catch (err) {
      enqueueSnackbar(workbookErrorMessage(err, "Could not load the loan's documents."), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [dealId, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const docs = useMemo(() => data?.documents || [], [data]);
  const statements = docs.filter((d) => d.isSettlementStatement);
  const others = docs.filter((d) => !d.isSettlementStatement);
  const chosen = docs.find((d) => d.documentId === choice);

  const docByFsn = useMemo(() => {
    const m = new Map();
    (loanDocuments || []).forEach((d) => {
      // Loan documents carry snake_case `file_storage_name`.
      const fsn = d?.file_storage_name || d?.fileStorageName;
      if (fsn) m.set(fsn, d);
    });
    return m;
  }, [loanDocuments]);

  const preview = (e, d) => {
    e.stopPropagation();
    const full = docByFsn.get(d.fileStorageName);
    if (full) dispatch(filePreviewSetData(full));
  };

  const runBuild = async () => {
    setConfirmReplace(false);
    setBuilding(true);
    setRejection(null);
    try {
      const res = await settlementWorkbookApi.build(dealId, choice, {
        source: mode,
        replace: exists,
      });
      if (res?.extraction && res.extraction.ok === false) {
        // Not a settlement statement (or the read failed): nothing was saved.
        setRejection(res.extraction.error || "The document could not be read as a settlement statement.");
        return;
      }
      enqueueSnackbar("Settlement workbook built", { variant: "success" });
      onBuilt(res);
    } catch (err) {
      enqueueSnackbar(workbookErrorMessage(err, "The workbook could not be built."), {
        variant: "error",
      });
    } finally {
      setBuilding(false);
    }
  };

  const docGroupFor = (docType) => {
    if (!groupsAndTypes || typeof groupsAndTypes !== "object") return undefined;
    const hit = Object.entries(groupsAndTypes).find(
      ([, types]) => Array.isArray(types) && types.includes(docType),
    );
    return hit ? hit[0] : undefined;
  };

  const onPickFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length || !uploadType) return;
    const docGroup = docGroupFor(uploadType);
    setUploading(true);
    await uploadLoanDocs({
      loanId: dealId,
      files: list.map((file) => ({
        file,
        id: `${file.name.replace(/\.[^.]+$/, "")}-${stamp()}${file.name.match(/\.[^.]+$/)?.[0] || ""}`,
        name: file.name,
        ...(docGroup ? { docGroup } : {}),
        docType: uploadType,
      })),
      onSuccessMsg: "Statement uploaded",
      onSuccessFn: () => {
        setUploading(false);
        getLoanById({ loanId: dealId, onSuccessFn: (loan) => dispatch(loanDrilldownSet(loan)) });
        load();
      },
      onFailFn: () => setUploading(false),
    });
  };

  const DocCard = ({ d }) => {
    const canPreview = docByFsn.has(d.fileStorageName);
    return (
      <FormChoiceCard
        selected={choice === d.documentId}
        disabled={building}
        onClick={() => setChoice(d.documentId)}
      >
        <Flx jb g={1.5} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Link
              component="button"
              type="button"
              disabled={!canPreview}
              onClick={(e) => preview(e, d)}
              sx={{ ...formSx.body, fontWeight: 500, textAlign: "left", wordBreak: "break-word" }}
            >
              {d.fileDisplayName || d.fileStorageName}
            </Link>
            <Txt sx={{ ...formSx.meta, display: "block", mt: "2px" }}>
              {[
                d.docType,
                d.uploadDate ? `Uploaded ${formatDateTime(d.uploadDate, "date")}` : null,
                d.parsed && d.extractedLineCount != null
                  ? `${d.extractedLineCount} lines extracted`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Txt>
          </Box>
          <Flx ac g={0.75} wrap sx={{ justifyContent: "flex-end" }}>
            {d.isWorkbookSource ? <FormPill tone="info">Current source</FormPill> : null}
            {d.docVariant ? <FormPill>{d.docVariant}</FormPill> : null}
            {d.isExecuted === true ? (
              <FormPill tone="success">Executed</FormPill>
            ) : d.isExecuted === false ? (
              <FormPill tone="warning">Preliminary</FormPill>
            ) : null}
            {d.rejected ? (
              <FormPill tone="error">Not a statement</FormPill>
            ) : d.parsed ? (
              <FormPill tone="neutral">Parsed</FormPill>
            ) : null}
          </Flx>
        </Flx>
      </FormChoiceCard>
    );
  };

  if (loading && !data) {
    return (
      <Flx center sx={{ p: 6 }}>
        <CircularProgress size={24} />
      </Flx>
    );
  }

  return (
    <Flx column gap={3}>
      <Box>
        <SectionHead label="1 · Choose the statement" end={`${statements.length} settlement statements`} />
        <Txt sx={{ fontSize: 12.5, color: T.muted, display: "block", my: 1 }}>
          Pick the loan's preliminary HUD / ALTA / settlement statement. Settlement statements
          are listed first; everything else follows in case one was misfiled.
        </Txt>
        <Flx column g={1}>
          {statements.map((d) => (
            <DocCard key={d.documentId} d={d} />
          ))}
          {!statements.length ? (
            <FormNote>No settlement statement is on this loan yet — upload one below.</FormNote>
          ) : null}
          {others.length ? (
            <details>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: T.muted, padding: "6px 0" }}>
                Other documents ({others.length})
              </summary>
              <Flx column g={1} sx={{ mt: 1 }}>
                {others.map((d) => (
                  <DocCard key={d.documentId} d={d} />
                ))}
              </Flx>
            </details>
          ) : null}
        </Flx>
      </Box>

      <Box>
        <SectionHead label="Or upload a new statement" />
        <Flx column g={1.25} sx={{ mt: 1.25 }}>
          <FormOptionChips
            options={data?.uploadDocTypes || []}
            value={uploadType}
            onChange={(v) => setUploadType(v || "")}
          />
          <input ref={fileRef} type="file" hidden multiple accept=".pdf,image/*" onChange={onPickFiles} />
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileOutlined />}
              disabled={!uploadType || uploading || building}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? "Uploading…" : "Choose file"}
            </Button>
          </Box>
        </Flx>
      </Box>

      <Box>
        <SectionHead label="2 · Build" />
        <Flx column g={1} sx={{ mt: 1.25 }}>
          <FormChoiceCard
            selected={mode === BUILD_SOURCES.WORKBOOK_EXTRACT}
            disabled={building}
            onClick={() => setMode(BUILD_SOURCES.WORKBOOK_EXTRACT)}
          >
            <Txt sx={formSx.body}>Read every line (recommended)</Txt>
            <Txt sx={{ ...formSx.meta, display: "block", mt: "2px" }}>
              Every line in four columns plus the printed totals and pages. Takes 30–90 seconds.
            </Txt>
          </FormChoiceCard>
          <FormChoiceCard
            selected={mode === BUILD_SOURCES.HUD_EXTRACT}
            disabled={building || (chosen && !chosen.parsed)}
            onClick={() => setMode(BUILD_SOURCES.HUD_EXTRACT)}
          >
            <Txt sx={formSx.body}>Quick seed from the existing extract</Txt>
            <Txt sx={{ ...formSx.meta, display: "block", mt: "2px" }}>
              Immediate, but less complete — the printed-total checks will usually show
              variances. Only for a statement that has already been parsed.
            </Txt>
          </FormChoiceCard>
          {rejection ? <FormWarn>{rejection}</FormWarn> : null}
          <Flx ac g={1.5} sx={{ mt: 0.5 }}>
            <Button
              variant="contained"
              disabled={!choice || building || (mode === BUILD_SOURCES.HUD_EXTRACT && chosen && !chosen.parsed)}
              onClick={() => (exists ? setConfirmReplace(true) : runBuild())}
              startIcon={building ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {building ? "Building…" : exists ? "Rebuild workbook" : "Build workbook"}
            </Button>
            {onCancel ? (
              <Button variant="text" color="gray" disabled={building} onClick={onCancel}>
                Cancel
              </Button>
            ) : null}
            {building && mode === BUILD_SOURCES.WORKBOOK_EXTRACT ? (
              <Txt sx={{ fontSize: 12.5, color: T.muted }}>
                Reading every line of the statement — this takes 30–90 seconds.
              </Txt>
            ) : null}
          </Flx>
        </Flx>
      </Box>

      <ConfirmationDialog
        open={confirmReplace}
        title="Rebuild the workbook?"
        message="A rebuild replaces the current workbook and discards every edit, grouping and note made on it."
        confirmText="Rebuild"
        destructive
        onConfirm={runBuild}
        onCancel={() => setConfirmReplace(false)}
      />
    </Flx>
  );
};

/* ─────────────────────────────── PDF pane ───────────────────────────────── */

const PdfPane = ({ dealId, fileStorageName, page }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let made = null;
    setUrl(null);
    setFailed(false);
    if (!fileStorageName) return undefined;
    // An iframe can't carry the auth header, so the bytes come down as a Blob
    // and the frame points at an object URL (doc § UI notes).
    losExtractsApi
      .getSourceBlobUrl(dealId, fileStorageName)
      .then((u) => {
        made = u;
        if (live) setUrl(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [dealId, fileStorageName]);

  return (
    <Box
      sx={{
        border: `1px solid ${T.line}`,
        borderRadius: T.radiusSm,
        overflow: "hidden",
        bgcolor: T.subtle,
        height: { xs: 480, lg: "calc(100vh - 260px)" },
        minHeight: 480,
        position: { lg: "sticky" },
        top: { lg: 16 },
      }}
    >
      {failed ? (
        <Flx center sx={{ height: "100%", p: 3 }}>
          <Txt sx={{ fontSize: 12.5, color: T.muted }}>The statement could not be loaded.</Txt>
        </Flx>
      ) : url ? (
        <iframe
          // A new hash re-points the viewer at the clicked line's page.
          key={`${url}#${page || 1}`}
          title="Settlement statement"
          src={`${url}#page=${page || 1}`}
          style={{ width: "100%", height: "100%", border: 0 }}
        />
      ) : (
        <Flx center sx={{ height: "100%" }}>
          <CircularProgress size={22} />
        </Flx>
      )}
    </Box>
  );
};

/* ───────────────────────────── balance panel ───────────────────────────── */

const BalancePanel = ({ wb }) => {
  const checks = wb?.printedTotalChecks || [];
  const cash = wb?.cashToCloseCheck || {};
  const wire = wb?.wireTieOut || {};
  const paidFrom = wb?.paidFromTotals || {};
  const groups = wb?.groupTotals || [];

  const Row = ({ label, sub, mark }) => (
    <Flx jb ac gap={1} sx={{ py: 0.75, borderBottom: `1px dashed ${T.line2}` }}>
      <Flx column sx={{ minWidth: 0 }}>
        <Txt sx={{ fontSize: 12.5, color: T.ink2 }}>{label}</Txt>
        {sub ? <Txt sx={{ fontSize: 11, color: T.faint }}>{sub}</Txt> : null}
      </Flx>
      {mark}
    </Flx>
  );

  return (
    <Flx column gap={2.5}>
      <Box>
        <SectionHead label="Printed totals" end="completeness" />
        {checks.length ? (
          checks.map((c) => (
            <Row
              key={c.column}
              label={COLUMN_LABEL[c.column] || c.column}
              sub={`printed ${money(c.printed)} · lines ${money(c.computed)}`}
              mark={<TieMark ties={c.ties} variance={c.variance} />}
            />
          ))
        ) : (
          <Txt sx={{ fontSize: 12, color: T.faint, display: "block", py: 1 }}>
            No printed totals were read.
          </Txt>
        )}
        <Txt sx={{ fontSize: 11, color: T.faint, display: "block", mt: 0.75 }}>
          A variance means a line is missing or duplicated.
        </Txt>
      </Box>

      <Box>
        <SectionHead label="Cash to close" />
        <Row
          label={Number(cash.computed) < 0 ? "Due to borrower" : "Due from borrower"}
          sub={`printed ${money(cash.printed)} · lines ${money(cash.computed)}`}
          mark={<TieMark ties={cash.ties} variance={cash.variance} />}
        />
      </Box>

      <Box>
        <SectionHead label="Wire to title" end="vs Net Wire Calculator" />
        <Row
          label="Implied by the statement"
          sub={`statement ${money(wire.impliedWireToTitle)} · calculator ${money(wire.netWireCalculatorWireToTitle)}`}
          mark={<TieMark ties={wire.ties} variance={wire.variance} />}
        />
        <Txt sx={{ fontSize: 11, color: T.faint, display: "block", mt: 0.75 }}>
          A comparison against the LOS terms — a variance here is a finding (the loan amount or
          fees differ from the statement), not an error.
        </Txt>
      </Box>

      <Box>
        <SectionHead
          label="By funding source"
          end={wb?.unassignedLineCount ? `${wb.unassignedLineCount} unassigned` : "all assigned"}
        />
        {Object.entries(paidFrom).map(([k, v]) => (
          <Flx key={k} jb sx={{ py: 0.5 }}>
            <Txt sx={{ fontSize: 12.5, color: k === "Unassigned" && v ? T.amber : T.ink2 }}>{k}</Txt>
            <Txt sx={{ fontFamily: T.mono, fontSize: 12.5, color: T.ink }}>{money(v)}</Txt>
          </Flx>
        ))}
      </Box>

      {groups.length ? (
        <Box>
          <SectionHead label="By group" />
          {groups.map((g) => (
            <Flx key={g.group} jb gap={1} sx={{ py: 0.5 }}>
              <Txt sx={{ fontSize: 12.5, color: T.ink2 }}>
                {g.group} <span style={{ color: T.faint }}>· {g.lineCount}</span>
              </Txt>
              <Txt sx={{ fontFamily: T.mono, fontSize: 12, color: T.ink }}>
                {money((g.borrowerDebit || 0) - (g.borrowerCredit || 0))}
              </Txt>
            </Flx>
          ))}
          <Txt sx={{ fontSize: 11, color: T.faint, display: "block", mt: 0.5 }}>
            Borrower debits less credits per group.
          </Txt>
        </Box>
      ) : null}
    </Flx>
  );
};

/* ─────────────────────────────── the tab ──────────────────────────────── */

const LoanClosingSubTabSettlementWorkbook = () => {
  const dealId = useSelector((s) => s.loanDrilldown?._id);
  const { enqueueSnackbar } = useSnackbar();
  const gridRef = useRef(null);

  const [options, setOptions] = useState(null);
  const [wb, setWb] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [showPdf, setShowPdf] = useState(true);

  // Every response carries the whole workbook — replace, never merge.
  const apply = useCallback((res) => {
    setExists(!!res?.exists);
    setWb(res?.workbook || null);
  }, []);

  const fail = useCallback(
    (err, fallback) =>
      enqueueSnackbar(workbookErrorMessage(err, fallback), { variant: "error" }),
    [enqueueSnackbar],
  );

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const [opts, res] = await Promise.all([
        settlementWorkbookApi.getOptions(),
        settlementWorkbookApi.get(dealId),
      ]);
      setOptions(opts);
      apply(res);
    } catch (err) {
      fail(err, "Could not load the settlement workbook.");
    } finally {
      setLoading(false);
    }
  }, [dealId, apply, fail]);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (fn, fallback) => {
      setBusy(true);
      try {
        apply(await fn());
      } catch (err) {
        fail(err, fallback);
        // Re-read so a rejected inline edit doesn't stay on screen.
        try {
          apply(await settlementWorkbookApi.get(dealId));
        } catch {
          /* keep what is shown */
        }
      } finally {
        setBusy(false);
      }
    },
    [apply, fail, dealId],
  );

  const lines = useMemo(() => wb?.lines || [], [wb]);
  const st = wb?.statement || {};
  const src = wb?.sourceDocument || {};

  const selectEditor = (values) => ({
    editable: true,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: { values: ["", ...(values || [])] },
    valueParser: (p) => (p.newValue === "" ? null : p.newValue),
  });
  const moneyCol = (field, headerName) => ({
    field,
    headerName,
    width: 120,
    editable: true,
    type: "rightAligned",
    cellClass: "mono-cell",
    valueFormatter: (p) => (p.value == null ? "" : money(p.value)),
    valueParser: (p) => toNumOrNull(p.newValue),
  });

  const columnDefs = useMemo(
    () => [
      {
        colId: "state",
        headerName: "",
        width: 64,
        pinned: "left",
        sortable: false,
        cellRenderer: (p) => {
          const l = p.data;
          const diffs = l?.edited ? diffLines(l) : [];
          return (
            <Flx ac jc gap={0.5} sx={{ height: "100%" }}>
              {l?.source === "manual" ? (
                <Tooltip title="Added by hand">
                  <span><Pill label="M" tone="info" /></span>
                </Tooltip>
              ) : null}
              {l?.edited ? (
                <Tooltip
                  title={
                    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                      {`Edited${l.editedBy ? ` by ${l.editedBy}` : ""}${l.editedAt ? ` · ${formatDateTime(l.editedAt, "datetime")}` : ""}\n${diffs.join("\n")}`}
                    </Box>
                  }
                >
                  <EditNoteRounded sx={{ fontSize: 17, color: T.amber }} />
                </Tooltip>
              ) : null}
              {l?.included === false ? (
                <Tooltip title="Excluded from totals">
                  <VisibilityOffOutlined sx={{ fontSize: 15, color: T.faint }} />
                </Tooltip>
              ) : null}
            </Flx>
          );
        },
      },
      { field: "page", headerName: "Pg", width: 64, editable: true, valueParser: (p) => toNumOrNull(p.newValue) },
      { field: "lineNumber", headerName: "Line", width: 80, editable: true },
      { field: "section", headerName: "Section", width: 150, ...selectEditor(options?.sections) },
      { field: "description", headerName: "Description", minWidth: 220, flex: 1, editable: true, tooltipField: "description" },
      { field: "lineCategory", headerName: "Category", width: 170, ...selectEditor(options?.lineCategories) },
      { field: "paidTo", headerName: "Paid to", width: 160, editable: true, tooltipField: "paidTo" },
      { field: "payeeRole", headerName: "Payee role", width: 140, ...selectEditor(options?.payeeRoles) },
      { field: "paidBy", headerName: "Paid by", width: 120, ...selectEditor(options?.paidBy) },
      moneyCol("borrowerDebit", "Borrower debit"),
      moneyCol("borrowerCredit", "Borrower credit"),
      moneyCol("sellerDebit", "Seller debit"),
      moneyCol("sellerCredit", "Seller credit"),
      moneyCol("pocAmount", "POC"),
      { field: "workbookGroup", headerName: "Group", width: 190, ...selectEditor(options?.workbookGroups) },
      { field: "paidFrom", headerName: "Paid from", width: 150, ...selectEditor(options?.paidFrom) },
      { field: "feesTabBucket", headerName: "Fees tab (preview)", width: 180, ...selectEditor(options?.feesTabBuckets) },
      { field: "note", headerName: "Note", width: 180, editable: true, tooltipField: "note" },
      actionColumn({
        colId: "revert",
        cellRenderer: (p) => (
          <ActionCell>
            <Tooltip
              title={
                p.data?.source === "manual"
                  ? "Added by hand — nothing to reset to"
                  : p.data?.edited
                    ? "Reset to extracted"
                    : "Unchanged from the extract"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={busy || p.data?.source === "manual" || !p.data?.edited}
                  onClick={(e) => {
                    e.stopPropagation();
                    run(
                      () => settlementWorkbookApi.revertLine(dealId, p.data.lineId),
                      "The line could not be reset.",
                    );
                  }}
                >
                  <UndoRounded sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          </ActionCell>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, busy, dealId, run],
  );

  // Inline edit → `update` for that one field, then replace from the response.
  const onCellValueChanged = useCallback(
    (p) => {
      const field = p.colDef.field;
      if (!field || p.oldValue === p.newValue) return;
      run(
        () => settlementWorkbookApi.updateLine(dealId, p.data.lineId, { [field]: p.newValue ?? null }),
        "The line could not be saved.",
      );
    },
    [dealId, run],
  );

  const groupSelected = (patch, msg) =>
    run(
      () => settlementWorkbookApi.group(dealId, { lineIds: selected, ...patch }),
      msg,
    ).then(() => gridRef.current?.api?.deselectAll());

  const [bulk, setBulk] = useState({ workbookGroup: "", paidFrom: "", feesTabBucket: "" });

  if (loading) {
    return (
      <PrimaryContentCard maxWidth={false} title="Settlement workbook">
        <Flx center sx={{ p: 6 }}>
          <CircularProgress />
        </Flx>
      </PrimaryContentCard>
    );
  }

  if (!exists || picking) {
    return (
      <PrimaryContentCard
        maxWidth={false}
        title={exists ? "Rebuild the settlement workbook" : "Settlement workbook"}
        subTitle="Balance the preliminary HUD / ALTA line by line before closing. Nothing here changes the loan or the Fees tab."
      >
        <SourcePicker
          dealId={dealId}
          exists={exists}
          onCancel={exists ? () => setPicking(false) : null}
          onBuilt={(res) => {
            apply(res);
            setPicking(false);
            setPage(1);
          }}
        />
      </PrimaryContentCard>
    );
  }

  const bulkSelect = (key, label, values) => (
    <TextField
      select
      size="small"
      value={bulk[key]}
      onChange={(e) => setBulk((b) => ({ ...b, [key]: e.target.value }))}
      SelectProps={{ displayEmpty: true }}
      sx={{ minWidth: 170 }}
      disabled={!selected.length || busy}
    >
      <MenuItem value="">
        <em>{label}…</em>
      </MenuItem>
      {(values || []).map((v) => (
        <MenuItem key={v} value={v}>
          {v}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <PrimaryContentCard
      maxWidth={false}
      title="Settlement workbook"
      subTitle="Every line of the preliminary statement — who pays it, who receives it, and where it goes. Nothing here changes the loan or the Fees tab."
      endContent={
        <Flx ac gap={1}>
          <Pill
            label={wb?.status === "reviewed" ? "Reviewed" : "Draft"}
            tone={wb?.status === "reviewed" ? "success" : "neutral"}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() =>
              run(
                () =>
                  settlementWorkbookApi.setStatus(
                    dealId,
                    wb?.status === "reviewed" ? "draft" : "reviewed",
                  ),
                "The status could not be changed.",
              )
            }
          >
            {wb?.status === "reviewed" ? "Back to draft" : "Mark reviewed"}
          </Button>
          <Tooltip title="Reload">
            <span>
              <IconButton size="small" disabled={busy} onClick={load}>
                <RefreshRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Flx>
      }
    >
      <Flx column gap={2.5}>
        {/* Source + statement header */}
        <HudSourceChip
          label="Built from"
          hudSource={{
            documentId: src.documentId,
            fileStorageName: src.fileStorageName,
            fileName: src.fileDisplayName,
            docType: src.docType,
            docVariant: st.docVariant,
            altaVariant: st.altaVariant,
            isExecuted: st.isExecuted,
          }}
          actions={
            <Button size="small" variant="text" disabled={busy} onClick={() => setPicking(true)}>
              Rebuild…
            </Button>
          }
        >
          <Txt sx={{ fontSize: 11.5, color: T.faint, display: "block" }}>
            The Net Wire Calculator balances against this document unless a different one is
            chosen on Wire &amp; Funding → Net Wire Tie-out.
          </Txt>
        </HudSourceChip>

        {st.reviewNotes ? (
          <FormWarn>
            <b>Extractor notes:</b> {st.reviewNotes}
          </FormWarn>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 2,
            px: 2,
            py: 1.5,
            bgcolor: T.subtle,
            border: `1px solid ${T.line}`,
            borderRadius: T.radiusSm,
          }}
        >
          {[
            ["Transaction", st.transactionType],
            ["Settlement date", formatDate(st.settlementDate, "—", "MMM d, yyyy")],
            ["Settlement agent", st.settlementAgent],
            ["Borrower", st.borrowerName],
            ["Seller", st.sellerName],
            ["Lender", st.lenderName],
            ["Loan amount", st.loanAmount != null ? money(st.loanAmount) : null],
            ["Property", (st.propertyAddresses || []).join("; ")],
            ["Built", [wb?.buildSource, wb?.createdBy, wb?.createdAt ? formatDateTime(wb.createdAt, "date") : null].filter(Boolean).join(" · ")],
          ].map(([k, v]) => (
            <Flx column key={k} gap={0.25} sx={{ minWidth: 0 }}>
              <Txt sx={{ ...labelSx, display: "block" }}>{k}</Txt>
              <Txt sx={{ fontSize: 12.5, color: v ? T.ink : T.faint, wordBreak: "break-word" }}>
                {v || "—"}
              </Txt>
            </Flx>
          ))}
        </Box>

        {/* Three panes: PDF · lines · balance */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            alignItems: "start",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              xl: showPdf
                ? "minmax(320px, 0.9fr) minmax(0, 2fr) 300px"
                : "minmax(0, 1fr) 300px",
            },
          }}
        >
          {showPdf ? (
            <PdfPane dealId={dealId} fileStorageName={src.fileStorageName} page={page} />
          ) : null}

          <Flx column gap={1} sx={{ minWidth: 0 }}>
            <Flx ac wrap gap={1}>
              <Button
                size="small"
                variant="text"
                startIcon={showPdf ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                onClick={() => setShowPdf((v) => !v)}
              >
                {showPdf ? "Hide PDF" : "Show PDF"}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Txt sx={{ fontSize: 12, color: T.muted }}>
                {selected.length ? `${selected.length} selected` : `${lines.length} lines`}
              </Txt>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRounded />}
                disabled={busy}
                onClick={() =>
                  run(
                    () => settlementWorkbookApi.addLine(dealId, { description: "New line", page }),
                    "The line could not be added.",
                  )
                }
              >
                Add line
              </Button>
            </Flx>

            {selected.length ? (
              <Flx
                ac
                wrap
                gap={1}
                sx={{ p: 1, bgcolor: T.accentTint, border: `1px solid ${T.line}`, borderRadius: T.radiusSm }}
              >
                {bulkSelect("workbookGroup", "Group", options?.workbookGroups)}
                {bulkSelect("paidFrom", "Paid from", options?.paidFrom)}
                {bulkSelect("feesTabBucket", "Fees tab (preview)", options?.feesTabBuckets)}
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy || (!bulk.workbookGroup && !bulk.paidFrom && !bulk.feesTabBucket)}
                  onClick={() => {
                    const patch = Object.fromEntries(Object.entries(bulk).filter(([, v]) => v));
                    groupSelected(patch, "The lines could not be grouped.").then(() =>
                      setBulk({ workbookGroup: "", paidFrom: "", feesTabBucket: "" }),
                    );
                  }}
                >
                  Apply
                </Button>
                <Button size="small" variant="outlined" disabled={busy} onClick={() => groupSelected({ included: true }, "Could not include the lines.")}>
                  Include
                </Button>
                <Button size="small" variant="outlined" disabled={busy} onClick={() => groupSelected({ included: false }, "Could not exclude the lines.")}>
                  Exclude
                </Button>
              </Flx>
            ) : null}

            <AgGridTableWrapper
              height="calc(100vh - 300px)"
              sx={{
                minHeight: 420,
                "& .mono-cell": { fontFamily: T.mono, fontSize: "12px" },
                "& .wb-excluded": { opacity: 0.5 },
              }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={lines}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true, sortable: true }}
                getRowId={(p) => String(p.data.lineId)}
                rowSelection={{ mode: "multiRow" }}
                singleClickEdit
                stopEditingWhenCellsLoseFocus
                onCellValueChanged={onCellValueChanged}
                onSelectionChanged={(e) =>
                  setSelected(e.api.getSelectedRows().map((r) => r.lineId))
                }
                onRowClicked={(e) => {
                  if (e.data?.page) setPage(e.data.page);
                }}
                rowClassRules={{ "wb-excluded": (p) => p.data?.included === false }}
                tooltipShowDelay={300}
              />
            </AgGridTableWrapper>
            <Txt sx={{ fontSize: 11.5, color: T.faint }}>
              Click a line to jump the PDF to its page; click a cell to edit it. Edited lines
              carry a note icon — hover it for what changed.
            </Txt>
          </Flx>

          <Box
            sx={{
              p: 2,
              border: `1px solid ${T.line}`,
              borderRadius: T.radiusSm,
              bgcolor: T.card,
              position: { xl: "sticky" },
              top: { xl: 16 },
            }}
          >
            <Flx ac gap={1} sx={{ mb: 1.5 }}>
              {(wb?.printedTotalChecks || []).some((c) => c.ties === false) ||
              wb?.cashToCloseCheck?.ties === false ? (
                <ReportProblemOutlined sx={{ fontSize: 18, color: T.red }} />
              ) : (
                <CheckCircleRounded sx={{ fontSize: 18, color: T.green }} />
              )}
              <Txt sx={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Balance</Txt>
              <Txt sx={{ fontSize: 11.5, color: T.faint }}>zero tolerance</Txt>
            </Flx>
            <BalancePanel wb={wb} />
          </Box>
        </Box>
      </Flx>
    </PrimaryContentCard>
  );
};

export default LoanClosingSubTabSettlementWorkbook;
